<template>
  <div>
    <q-card
      v-if="sessionToShow && sessionToShow.notes"
      class="q-mb-md"
    >
      <q-card-section class="text-subtitle2">
        {{ $t('run.overview.textMessageFrom') }} {{ sessionToShow.createdByName }}
      </q-card-section>
      <q-card-section class="q-pt-none">
        <span class="instructions-message">{{ sessionToShow.notes }}</span>
      </q-card-section>
    </q-card>

    <div class="row q-col-gutter-md">
      <template v-if="isLoading">
        <div
          v-for="sessionProgramSkeleton in 6"
          :key="sessionProgramSkeleton"
          class="col-md-4 col-sm-6 col-xs-12"
        >
          <q-card class="full-height cursor-pointer">
            <q-skeleton
              height="150px"
              square
            />
            <q-card-section>
              <q-skeleton
                type="text"
                class="text-subtitle1"
              />
              <q-skeleton
                type="text"
                class="text-subtitle1"
              />
              <q-skeleton
                type="text"
                class="text-caption"
              />
              <q-skeleton
                type="text"
                class="text-caption"
              />
              <q-skeleton
                type="text"
                class="text-caption"
              />
            </q-card-section>
          </q-card>
        </div>
      </template>

      <template v-else>
        <div
          v-for="program in shownSessionPrograms"
          :key="program.id"
          class="col-md-4 col-sm-6 col-xs-12"
        >
          <q-card
            class="full-height cursor-pointer"
            @click="onShowProgramInfo(program)"
          >
            <q-img
              v-if="program.coverphoto"
              :src="program.coverphoto"
              no-transition
              :ratio="24/9"
            >
              <div class="absolute-bottom text-h6 text-white">
                {{ program.programName }}
              </div>
            </q-img>

            <q-card-section>
              <div
                v-if="!program.coverphoto"
                class="text-h6 q-pb-md"
              >
                {{ program.programName }}
              </div>

              <div
                v-if="program.objective"
                class="text-subtitle2 ellipsis-3-lines q-mb-md"
              >
                {{ personalize(program.objective, client) }}
              </div>

              <div
                class="instructions-teaser editor-content"
                v-html="personalize(program.instructions, client)"
              />
            </q-card-section>
          </q-card>
        </div>
      </template>
    </div>

    <div>
      <activity-feed
        v-if="!hasNoPrograms"
        :events="['programModification', 'programCreation']"
        hide-empty
        dense
        clickable
        :filter="[['eventInfo.programId', 'in', activeProgramsIds]]"
        :client-id="clientId"
        @click="onClickProgramMod"
      >
        <template #header>
          <div class="row q-my-md col text-overline text-dark text-uppercase">
            {{ $t('run.overview.textProgramMods') }}
          </div>
        </template>
      </activity-feed>
    </div>
  </div>
</template>

<script>
import ActivityFeed from 'components/Activity/ActivityFeed';
import ProgramInfo from 'components/Programs/ProgramInfo';
import { chain, find, get, isEmpty, map } from 'lodash';
import { mapState } from 'pinia';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import ProgramsDB from 'src/fb/programs-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import Interactions from 'src/mixins/Interactions';
import Personalize from 'src/mixins/Personalize';
import { auth } from 'src/store/auth';
import { programs } from 'src/store/programs';

export default {
  name: 'SessionPreparationContent',

  components: {
    ActivityFeed,
  },

  mixins: [
    Interactions,
    Personalize,
  ],

  props: {
    sessionId: {
      type: String,
      default: null,
    },
    clientId: {
      type: String,
      default: null,
    },
    preLoadSession: {
      type: Object,
      default: null,
    },
    preLoadPrograms: {
      type: Array,
      default: () => [],
    },
    client: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      session: null,
      programsInSession: [],
      clientPrograms: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'hasSupervisorCredentials']),
    ...mapState(programs, ['programs']),

    shownSessionPrograms() {
      if (this.preLoadPrograms && this.preLoadPrograms.length) {
        return this.preLoadPrograms;
      }

      return chain(this.programsInSession)
        .map((programId) => find(this.clientPrograms, { id: programId }))
        .compact()
        .filter((program) => ACTIVE_PROGRAM_STATUSES.includes(program.status))
        .value();
    }, // shownSessionPrograms

    sessionToShow() {
      return this.preLoadSession || this.session;
    }, // sessionToShow

    hasNoPrograms() {
      return isEmpty(this.activeProgramsIds);
    }, // hasNoPrograms

    activeProgramsIds() {
      return map(this.shownSessionPrograms, 'id');
    }, // activeProgramsIds
  },

  async mounted() {
    this.setIsLoading(true);

    if (this.preLoadSession) {
      this.session = this.preLoadSession;
    } else if (this.sessionId) {
      await this.loadSession(this.sessionId);
    }

    if ((!this.preLoadPrograms || !this.preLoadPrograms.length) && this.sessionId) {
      await this.loadSessionPrograms(this.sessionId);
      await this.loadClientPrograms();
    }

    this.setIsLoading(false);
  },

  methods: {
    async loadSession(sessionId) {
      const sessionsDb = new SessionsDB(sessionId);
      this.session = await sessionsDb.read(sessionId);

      if (get(this.session, 'isDeleted') === true) {
        this.notifyNegative(this.$t('run.overview.notifySessionDeleted'));
        this.$router.push('/');
        throw new Error('Session is deleted');
      }

      this.checkSessionVisibility();
    }, // loadSession()

    async loadSessionPrograms(sessionId) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);

      this.programsInSession = chain(await sessionProgramsDb.readAll())
        .sortBy('order')
        .reject({ hidden: true })
        .map('program')
        .uniq()
        .value();
    }, // loadSessionPrograms()

    async loadClientPrograms() {
      if (!this.clientId && !this.currentClient.id) {
        this.clientPrograms = [];
        return this.clientPrograms;
      }

      if (this.clientId !== this.currentClient.id) {
        const programsDb = new ProgramsDB();
        this.clientPrograms = await programsDb.readAll([
          ['client', '==', this.clientId],
          ['isDeleted', '==', false],
        ]);
      } else {
        this.clientPrograms = this.programs;
      }
      return this.clientPrograms;
    }, // loadClientPrograms()

    onShowProgramInfo(program) {
      this.openComponentDialog(ProgramInfo, { program, client: this.client });
    }, // onShowProgramInfo()

    onClickProgramMod(activity) {
      const programId = get(activity, ['eventInfo', 'programId'], null);
      if (!programId) {
        return;
      }
      const searchingPrograms = this.clientId && this.clientId !== this.currentClient.id
        ? this.clientPrograms
        : this.programs;

      const program = find(searchingPrograms, { id: programId });
      if (program) {
        this.onShowProgramInfo(program);
      }
    }, // onClickProgramMod()

    checkSessionVisibility() {
      const { limitedVisibility, visibility } = this.session;
      const isSessionVisible = !limitedVisibility || this.hasSupervisorCredentials
        || visibility.includes(this.userDetails.userPrivilege || this.userDetails.role);

      if (isSessionVisible) {
        return;
      }

      return this.$q.dialog({
        message: this.$t('run.overview.dialogSessionVisibilityLimited'),
        cancel: false,
        persistent: true,
      }).onOk(async () => {
        this.$router.push('/');
      });
    }, // checkSessionVisibility()

    setIsLoading(value) {
      this.isLoading = value;
    }, // setIsLoading()
  },
};
</script>

<style scoped>
.instructions-message {
  white-space: pre-line;
}
.instructions-teaser {
  margin-bottom: 5px;
  padding-bottom: 5px;
  max-height:96px;
  overflow:hidden;
  text-overflow: ellipsis;
  content: "";
  position:relative;
}
.instructions-teaser:before {
  content:'';
  width:100%;
  height:100%;
  position:absolute;
  left:0;
  top:0;
  background:linear-gradient(transparent 50px, white);
}
</style>
